import { AxiosError } from "axios";

export interface ApiErrorResponse {
  status: number;
  message: string;
  details?: Record<string, string[]>;
}

/**
 * Type guard to check if an error is an ApiErrorResponse
 */
export const isApiErrorResponse = (
  error: unknown,
): error is ApiErrorResponse => {
  if (typeof error !== "object" || error === null) return false;
  const maybeError = error as Record<string, unknown>;
  return (
    typeof maybeError.status === "number" &&
    typeof maybeError.message === "string"
  );
};

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((item) => typeof item === "string");

const normalizeMessage = (value: unknown): string | undefined => {
  if (isStringArray(value) && value.length > 0) return value.join(", ");
  return typeof value === "string" ? value : undefined;
};

const isValidationDetails = (
  value: unknown,
): value is Record<string, string[]> =>
  typeof value === "object" &&
  value !== null &&
  !Array.isArray(value) &&
  Object.values(value).every(isStringArray);

/**
 * Extracts a user-friendly error message from an API error response
 * Handles various response formats:
 * - Plain text responses
 * - JSON responses with error/message field
 * - JSON responses with nested errors
 */
export const extractErrorMessage = (error: unknown): ApiErrorResponse => {
  // Default error response
  const defaultError: ApiErrorResponse = {
    status: 500,
    message: "An unexpected error occurred. Please try again.",
  };

  if (!error) {
    return defaultError;
  }

  // Handle Axios errors
  if (error instanceof AxiosError) {
    const status = error.response?.status || 500;
    const responseData = error.response?.data;

    // Handle plain text responses
    if (typeof responseData === "string") {
      return {
        status,
        message: responseData,
      };
    }

    // Handle JSON responses
    if (responseData && typeof responseData === "object") {
      const data = responseData as Record<string, unknown>;
      // Check for error/message in response data
      const message =
        normalizeMessage(data.error) ??
        normalizeMessage(data.errors) ??
        normalizeMessage(data.message) ??
        error.message ??
        defaultError.message;

      const rawDetails = data.details ?? data.errors;
      const details = isValidationDetails(rawDetails) ? rawDetails : undefined;

      return {
        status,
        message,
        ...(details && { details }),
      };
    }

    // If we have a status-specific message, use it
    if (status === 401) {
      return {
        status,
        message: "Your session has expired. Please log in again.",
      };
    }

    if (status === 403) {
      return {
        status,
        message: "You do not have permission to perform this action.",
      };
    }

    if (status === 404) {
      return {
        status,
        message: "The requested resource was not found.",
      };
    }

    return {
      status,
      message: error.message || defaultError.message,
    };
  }

  // Handle standard Error objects
  if (error instanceof Error) {
    return {
      status: 500,
      message: error.message,
    };
  }

  return defaultError;
};

/**
 * Formats validation errors into a user-friendly message
 */
export const formatValidationErrors = (
  details: Record<string, string[]>,
): string =>
  Object.entries(details)
    .map(([field, errors]) => {
      const fieldName = field
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
      return `${fieldName}: ${errors.join(", ")}`;
    })
    .join("\n");

/**
 * Extracts an error message from a mutation error object
 * Handles the common React Query mutation error format:
 * - Array of error strings: joins them with ", "
 * - Single message string: returns as-is
 * - Fall back to "Unknown error" if neither is present
 */
export const getErrorMessage = (error: unknown): string => {
  const err = error as { errors?: string[]; message?: string };
  if (Array.isArray(err?.errors)) return err.errors.join(", ");
  // An Axios rejection keeps the API's own words in the response body; its
  // `message` is only ever "Request failed with status code 422", which tells a
  // customer nothing about why their pay period did not lock.
  if (error instanceof AxiosError) return extractErrorMessage(error).message;
  return err?.message ?? "Unknown error";
};
